import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { BookIcon } from '@/components/game/GameIcons'

/**
 * Pilule flottante « Explorer le Codex » de la landing publique. Elle persiste
 * en bas de l'écran pendant tout le défilement pour guider le visiteur vers les
 * règles du jeu, puis s'efface dès que le vrai bouton « Consulter le Codex »
 * (#codex-cta) entre dans le viewport — plus la peine de doubler l'appel à
 * l'action, et ça couvre aussi le cas « tout en bas ».
 *
 * Clic → défile en douceur jusqu'au bloc Codex (respecte prefers-reduced-motion).
 */
export function CodexExplorerFab({ targetId = 'codex-cta' }: { targetId?: string }) {
  const [visible, setVisible] = useState(true)
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    const target = document.getElementById(targetId)
    if (!target) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0.2 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [targetId])

  const scrollToCodex = () => {
    document.getElementById(targetId)?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'center',
    })
  }

  return (
    <div
      aria-hidden={!visible}
      className={`codex-fab-dock flex justify-center px-4 transition-all duration-500 ${
        visible ? '' : 'opacity-0 translate-y-6 pointer-events-none'
      }`}
    >
      <button
        type="button"
        onClick={scrollToCodex}
        tabIndex={visible ? 0 : -1}
        className="pointer-events-auto inline-flex items-center gap-2.5 rounded-full bg-gradient-to-b from-sand-300 to-sand-500
                   px-6 py-3 font-display text-base font-bold text-ocean-900 ring-1 ring-sand-100/60 shadow-lg shadow-sand-500/30
                   transition-transform hover:-translate-y-0.5 active:translate-y-0"
      >
        <BookIcon className="w-5 h-5" />
        Explorer le Codex
        <svg
          viewBox="0 0 24 24"
          className="codex-fab-arrow w-4 h-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 10l6 6 6-6" />
        </svg>
      </button>
    </div>
  )
}
